import { EXISTING_VARIANT_GROUP, type VariantExportSelection } from '@ikegps/core-api';
import { makeObservable, observable } from 'mobx';
import { CheckBox, type CheckBoxState } from '../../components/input/input.checkbox';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { ModalFormComponent } from '../../components/modal.form/modal.form.component';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS, Style } from './export.variants.modal.style';

addStyles(Style);

export interface ExportTypeOption {
	/** Translated label shown (and used as the option value) in the "Export type" select. */
	label: string;
	action: () => unknown;
	/** When present, Export invokes this with the checked variant selections instead of `action`. */
	variantAction?: (selections: VariantExportSelection[]) => unknown;
	/** When true, every "Include variants" checkbox but Existing is greyed out for this type. */
	disableVariants?: boolean;
}

/**
 * A single "Include variants" checkbox. `variantType`/`variantTypeName` are used for filtering
 * (empty `variantType` for the synthetic "Existing" checkbox, else the variant's own pair — see
 * `VariantExportSelection`); `label` is the already-resolved text to display (translated for
 * "Existing", or the variant's own `variantTypeName`/`variantType` for the rest).
 */
export interface VariantCheckboxOption extends VariantExportSelection {
	/** Unique key for checkbox state/list rendering — see `variantSelectionKey`. Two checkboxes
	 *  can share a `variantType` (differing only in `variantTypeName`), so `variantType` alone
	 *  can't be used as the key. */
	key: string;
}

/**
 * Shown instead of the export dropdown when the department has the PoleOS-verify-integration
 * token and the selection includes a pole with variants. The "Include variants" checkbox list is
 * rebuilt from the current selection every time the modal opens — "Existing" is always present,
 * plus one checkbox per distinct `(variantType, variantTypeName)` pair actually found in the
 * selection (there's no fixed set: `variantType` is an open union defined by PoleOS, and two
 * variants can share a type but differ in display name). Every export type either supplies a
 * `variantAction` — one file per checked group — or declares `disableVariants` (Importable Excel),
 * which greys the checkboxes out. An entry with neither falls back to `action`, which ignores the
 * checkboxes and exports the existing layer only with no warning — so wire new export types to one
 * or the other.
 */
export class ExportVariantsModal extends ModalFormComponent {
	typeSelect: SelectInputState;
	private getOptions: () => ExportTypeOption[];
	private getVariantOptions: () => VariantCheckboxOption[];
	private variantOptions = observable.array<VariantCheckboxOption>([]);
	private checkedVariants = observable.map<string, boolean>();

	constructor(getOptions: () => ExportTypeOption[], getVariantOptions: () => VariantCheckboxOption[]) {
		super({
			title: T.EXPORT_COLLECTIONS_MODAL_TITLE,
			confirm: T.EXPORT,
			success: { key: T.EXPORT_VARIANTS_STARTED },
		});
		makeObservable(this);

		this.getOptions = getOptions;
		this.getVariantOptions = getVariantOptions;
		this.displayCustomButtons = true;

		this.typeSelect = new SelectInputState(T.EXPORT_TYPE_LABEL);
		this.typeSelect.isLabelShown = false;
	}

	override open() {
		const options = this.getOptions();
		this.typeSelect.setOptions(options.map((option) => ({ key: option.label, value: option.label })));
		this.typeSelect.setValue(options[0]?.label ?? null);
		this.typeSelect.isRequired = true;

		const variantOptions = this.getVariantOptions();
		this.variantOptions.replace(variantOptions);
		this.checkedVariants.replace(variantOptions.map((option) => [option.key, true]));

		super.open();
	}

	get nodes(): MithrilVNode {
		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.row }, [
				m('div', { className: CSS.rowLabel }, Translate(T.EXPORT_TYPE_LABEL)),
				SelectInputComponent.view(this.typeSelect),
			]),
			m('div', { className: CSS.sectionLabel }, Translate(T.EXPORT_INCLUDE_VARIANTS_LABEL)),
			m(
				'div',
				this.variantOptions.map((option) => this.viewVariantRow(option))
			),
			m('p', { className: CSS.help }, Translate(T.EXPORT_VARIANTS_HELP)),
		]);
	}

	/**
	 * True when the selected export type opts into disabling variants (e.g. Importable Excel,
	 * which can't represent variants at all) — every checkbox but "Existing" is greyed out.
	 * Types that simply haven't been wired to `variantAction` yet (e.g. plain Excel) are
	 * unaffected: their checkboxes stay enabled, even though checking them does nothing yet.
	 */
	private variantsDisabled(): boolean {
		const selected = this.getOptions().find((option) => option.label === this.typeSelect.value);
		return selected?.disableVariants === true;
	}

	private viewVariantRow(option: VariantCheckboxOption): MithrilVNode {
		const isExisting = option.variantType === EXISTING_VARIANT_GROUP;
		const disabled = !isExisting && this.variantsDisabled();

		const checkboxState: CheckBoxState = {
			// Disabled checkboxes always show unchecked (a cue that this type won't be included)
			// without touching the stored state, so switching back to a variant-capable type
			// restores whatever the user had picked.
			isSelected: () => !disabled && this.checkedVariants.get(option.key) === true,
			setSelected: (value: boolean) => this.checkedVariants.set(option.key, value),
			isDisabled: disabled,
		};
		const className = disabled ? `${CSS.variantRow} ${CSS.variantRowDisabled}` : CSS.variantRow;
		return m('div', { className, key: option.key }, [CheckBox.view(checkboxState), m('div', option.label)]);
	}

	override customButtons(): MithrilVNode[] {
		return [
			m(
				'button',
				{ className: 'mdl-button', onclick: () => this.close(), disabled: this.loading, type: 'button' },
				Translate(T.CANCEL)
			),
			m(
				'button',
				{
					className: 'mdl-button mdl-button--accent',
					onclick: () => this.handleExport(),
					disabled: !this.typeSelect.value,
					type: 'button',
				},
				Translate(T.EXPORT)
			),
		];
	}

	/** The checked checkboxes, sent to the server as-is so the resulting file matches what's shown here. */
	private checkedSelections(): VariantExportSelection[] {
		return this.variantOptions
			.filter((option) => this.checkedVariants.get(option.key) === true)
			.map((option) => ({
				variantType: option.variantType,
				variantTypeName: option.variantTypeName,
				label: option.label,
			}));
	}

	private handleExport() {
		const selected = this.getOptions().find((option) => option.label === this.typeSelect.value);
		this.close();
		if (selected?.variantAction != null) {
			selected.variantAction(this.checkedSelections());
			return;
		}
		selected?.action();
	}
}
